import axios from "axios";
import * as chrono from "chrono-node";
import { AnimatePresence, motion } from "framer-motion";
import { gapi } from "gapi-script";
import { useEffect, useRef, useState } from "react";
import { FaCalendar, FaFilter } from "react-icons/fa6";
import { LiaNotesMedicalSolid } from "react-icons/lia";
import { useNavigate } from "react-router-dom";
import LoaderSpiner from "../../../Components/LoaderSpiner";
import CalComponent from "../../Demoform/CalComponent";
import styles from "./CallHistory.module.css";

// Schema for validating incoming data
const callDataSchema = {
  _id: { type: "string", required: true },
  campaignId: { type: "string", required: true },
  name: { type: "string", required: false, default: "Unknown" },
  email: { type: "string", required: false, default: "" },
  phoneNumber: { type: "string", required: false, default: "N/A" },
  callStatus: {
    type: "string",
    required: true,
    default: "Not initiated",
    allowedValues: ["Not initiated", "Joined", "Unjoined"],
  },
  callDuration: { type: "number", required: false, default: 0 },
  shortSummary: { type: "string", required: false, default: "N/A" },
  callerId: { type: ["string", "array"], required: false, default: [] },
  dealStatus: { type: "string", required: false, default: "N/A" },
  notes: { type: "array", required: false, default: [] },
  leadType: { type: "string", required: false, default: "N/A" },
  callTime: { type: "string", required: false, default: "N/A" },
  vendorId: { type: "string", required: true },
  other: { type: "object", required: false, default: {} },
  createdAt: {
    type: "string",
    required: true,
    default: new Date().toISOString(),
  },
  updatedAt: {
    type: "string",
    required: true,
    default: new Date().toISOString(),
  },
  __v: { type: "number", required: false, default: 0 },
};

// Validate and sanitize incoming data
const sanitizeCallData = (entry) => {
  const sanitized = {};
  for (const [key, config] of Object.entries(callDataSchema)) {
    let value = entry[key];

    if (value === undefined || value === null) {
      if (config.required && config.default !== undefined) {
        value = config.default;
      } else if (config.required) {
        throw new Error(`Missing required field: ${key}`);
      } else {
        value = config.default || null;
      }
    }

    if (key === "callerId") {
      if (Array.isArray(value)) {
        value = value.filter(
          (id) => id && typeof id === "string" && id.trim() !== ""
        );
        if (value.length === 0) value = config.default;
      } else if (typeof value !== "string") {
        console.warn(
          `Invalid type for ${key}. Expected string or array, got ${typeof value}`
        );
        value = config.default || [];
      } else {
        value = [value];
      }
    } else if (key === "notes") {
      if (!Array.isArray(value)) {
        value = [];
      } else {
        value = value.map((noteObj) => ({
          note: typeof noteObj.note === "string" ? noteObj.note : "",
          timestamp: noteObj.timestamp
            ? new Date(noteObj.timestamp).toISOString()
            : new Date().toISOString(),
        }));
      }
    } else if (value !== null && !config.type.includes(typeof value)) {
      console.warn(
        `Invalid type for ${key}. Expected ${config.type}, got ${typeof value}`
      );
      value = config.default || null;
    }

    if (config.allowedValues && !config.allowedValues.includes(value)) {
      console.warn(
        `Invalid value for ${key}. Expected one of ${config.allowedValues}, got ${value}`
      );
      value = config.default || "Not initiated";
    }

    if (key === "other" && value && typeof value === "object") {
      const sanitizedOther = {};
      for (const [otherKey, otherValue] of Object.entries(value)) {
        const newKey = isNaN(otherKey) ? otherKey : `field_${otherKey}`;
        sanitizedOther[newKey] = otherValue;
      }
      value = sanitizedOther;
    }

    if (key === "email" && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      value = config.default;
    }

    if (
      ["createdAt", "updatedAt"].includes(key) &&
      value &&
      !isValidISODate(value)
    ) {
      value = config.default;
    }

    sanitized[key] = value;
  }
  return sanitized;
};

// Helper to validate ISO 8601 date
const isValidISODate = (dateString) => {
  const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;
  return (
    isoDateRegex.test(dateString) && !isNaN(new Date(dateString).getTime())
  );
};

// Transform API response into table-compatible format
const getCallDataFromApi = (entry, campaignId, index, leadAnalysisData) => {
  const sanitizedEntry = sanitizeCallData(entry);
  const nameParts = sanitizedEntry.name
    ? sanitizedEntry.name.split(" ")
    : ["Unknown"];
  const firstName = nameParts[0] || "Unknown";
  const lastName = nameParts[1] || "";
  const leadAnalysis =
    leadAnalysisData?.find((la) =>
      sanitizedEntry.callerId.includes(la.callerId)
    ) || {};
  return {
    id: sanitizedEntry._id || `call_${index + 1}`,
    firstName,
    lastName,
    name: sanitizedEntry.name || "Unknown",
    number: sanitizedEntry.phoneNumber || "N/A",
    callStatus: sanitizedEntry.callStatus || "Not initiated",
    callDuration: sanitizedEntry.callDuration || 0,
    shortSummary: sanitizedEntry.shortSummary || "N/A",
    gender: sanitizedEntry.other?.gender || "N/A",
    startTime: sanitizedEntry.createdAt
      ? new Date(sanitizedEntry.createdAt).toLocaleString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
        timeZone: "Asia/Kolkata",
      })
      : "N/A",
    email: sanitizedEntry.email || `${firstName.toLowerCase()}@example.com`,
    callerId: sanitizedEntry.callerId || [],
    callRating:
      sanitizedEntry.other?.["0"] || sanitizedEntry.other?.field_0 || "N/A",
    notes: sanitizedEntry.notes || [],
    dealStatus: sanitizedEntry.dealStatus || "N/A",
    leadType: leadAnalysis.leadType || "N/A",
    campaignId: sanitizedEntry.campaignId,
    createdAt: sanitizedEntry.createdAt || new Date().toISOString(),
  };
};

// Utility to format duration from seconds to minutes/seconds
const formatDuration = (seconds) => {
  if (!seconds) return "0s";
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  if (minutes > 0) {
    return `${minutes}m ${remainingSeconds}s`;
  }
  return `${remainingSeconds}s`;
};

// Format notes for display
const formatNotesForDisplay = (notes) => {
  if (!Array.isArray(notes) || notes.length === 0) return "No notes";
  return notes
    .map((noteObj) =>
      noteObj.note
        ? `${noteObj.note} (${new Date(noteObj.timestamp).toLocaleString(
          "en-US",
          {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
            timeZone: "Asia/Kolkata",
          }
        )})`
        : ""
    )
    .filter((note) => note)
    .join(" ");
};

// FilterMenu component
const FilterMenu = ({ options, onSelect, onClose, currentFilter }) => {
  const menuRef = useRef(null);

  return (
    <div className={styles.filterMenu} ref={menuRef}>
      {options.map((option) => (
        <div
          key={option.value}
          className={`${styles.filterOption} ${currentFilter === option.value ? styles.activeFilter : ""
            }`}
          onClick={() => {
            onSelect(option.value);
            onClose();
          }}
        >
          {option.label}
        </div>
      ))}
    </div>
  );
};

// GoogleCalendarScheduler component
const GoogleCalendarScheduler = ({ call, onClose }) => {
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [textCommand, setTextCommand] = useState(
    `Schedule a meeting tomorrow at 10:30 AM for 30min with ${call.email || ""}`
  );

  const signIn = () => {
    gapi.auth2.getAuthInstance().signIn();
  };

  const parseCommand = (input) => {
    const emails = [
      ...input.matchAll(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g),
    ].map((match) => match[0]);
    const durationMatch = input.match(
      /(?:for|by|lasting)\s(\d+)\s?(minutes|min)?/i
    );

    const parsedDate = chrono.parseDate(input, { forwardDate: true });
    if (!parsedDate || !emails.length) return null;

    const duration = durationMatch ? parseInt(durationMatch[1]) : 30;
    const startDateTime = parsedDate;
    const endDateTime = new Date(startDateTime.getTime() + duration * 60000);

    return {
      startDateTime,
      endDateTime,
      emails,
      timeZone: "Asia/Kolkata",
    };
  };

  const scheduleFromText = async (e) => {
    e.preventDefault();
    const parsed = parseCommand(textCommand);
    if (!parsed) {
      console.log(
        "Could not parse input. Try something like:\n'Schedule a meeting tomorrow at 10:30 AM for 30min with abc@gmail.com'"
      );
      return;
    }

    const { startDateTime, endDateTime, emails } = parsed;

    const event = {
      summary: `Follow-up: ${call.name}`,
      description: `
    Hi,
    
    This is a follow-up meeting scheduled for ${call.name
        } regarding the recent call (ID: ${call.id}).
    
    📅 Date: ${startDateTime.toLocaleDateString()}
    🕒 Time: ${startDateTime.toLocaleTimeString()} - ${endDateTime.toLocaleTimeString()}
    ⏱ Duration: ${Math.round((endDateTime - startDateTime) / 60000)} minutes
    
    Please make sure to be available. If you have any queries, feel free to reply to this invitation.
    
    Thanks,
    ToxBox
      `.trim(),
      start: {
        dateTime: startDateTime.toISOString(),
        timeZone: "Asia/Kolkata",
      },
      end: {
        dateTime: endDateTime.toISOString(),
        timeZone: "Asia/Kolkata",
      },
      attendees: emails.map((email) => ({ email })),
    };

    try {
      const response = await gapi.client.calendar.events.insert({
        calendarId: "primary",
        resource: event,
        sendUpdates: "all",
      });

      console.log("Event created:", response);
      console.log("Meeting scheduled and invites sent!");
      onClose();
    } catch (error) {
      console.error("Error scheduling event:", error);
      console.log("Failed to schedule meeting.");
    }
  };

  return (
    <motion.div
      className={styles.schedulerModal}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.3 }}
    >
      <div className={styles.schedulerHeader}>
        <h3>Schedule Meeting for {call.name}</h3>
        <button onClick={onClose} className={styles.closeBtn}>
          ✕
        </button>
      </div>
      <div className={styles.schedulerContent}>
        {!isSignedIn ? (
          <button onClick={signIn} className={styles.signInButton}>
            Sign in with Google
          </button>
        ) : (
          <div className={styles.schedulerForm}>
            <textarea
              placeholder={`e.g. "Schedule a meeting tomorrow at 10:30 AM for 30min with ${call.email || "abc@gmail.com"
                }"`}
              value={textCommand}
              onChange={(e) => setTextCommand(e.target.value)}
              className={styles.schedulerTextarea}
              rows={4}
            />
            <button
              onClick={scheduleFromText}
              className={styles.scheduleButton}
            >
              Schedule Meeting
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
};

// NotesPopup component
const NotesPopup = ({ call, notes, onNotesChange, onNotesBlur, onClose }) => {
  const popupRef = useRef(null);

  return (
    <motion.div
      className={styles.notesModal}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.3 }}
      ref={popupRef}
    >
      <div className={styles.notesHeader}>
        <h3>Notes for {call.name}</h3>
        <button
          onClick={() => {
            onClose();
            onNotesBlur(call.id);
          }}
          className={styles.closeBtn}
        >
          ✕
        </button>
      </div>
      <div className={styles.notesContent}>
        <textarea
          value={notes[call.id] || ""}
          onChange={(e) => onNotesChange(call.id, e.target.value)}
          className={styles.notesTextarea}
          rows={4}
          placeholder="Add new note..."
        />
        <div className={styles.notesList}>
          {formatNotesForDisplay(call.notes)}
        </div>
      </div>
    </motion.div>
  );
};

// CallDetails component
const CallDetails = ({
  callerIds,
  campaignId,
  onClose,
  callData: parentCallData,
}) => {
  const [activeTab, setActiveTab] = useState("transcript");
  const [selectedCallerId, setSelectedCallerId] = useState(callerIds[0] || "");
  const [callData, setCallData] = useState({});
  const [loading, setLoading] = useState({});
  const [error, setError] = useState({});
  const [campaignData, setCampaignData] = useState(null);

  const authData = JSON.parse(localStorage.getItem("authData") || "{}");
  const token = authData?.token;

  // Fetch campaign data
  useEffect(() => {
    const fetchCampaign = async () => {
      try {
        if (!token) {
          throw new Error("No authentication token found");
        }

        const response = await axios.get(
          `https://prod.toxboxai.com/api/campaign/getsinglecampaign/${campaignId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        setCampaignData(response.data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching campaign:", err);
        setError("Failed to fetch campaign data: " + err.message);
        setLoading(false);
      }
    };

    fetchCampaign();
  }, [campaignId]);

  // Extract userId and vendorId after campaignData is available
  const userId =
    campaignData?.data?.campaign?.assignedParticipants?.[0] || null;
  const vendorId = campaignData?.data?.campaign?.vendorId || null;

  const BASE_API_URL =
    "https://prod.toxboxai.com/api/call-history/files";
  const S3_BASE_URL = "https://toxbox-images.s3.ap-south-1.amazonaws.com";

  // Fetch call data when campaignData, userId, and vendorId are available
  useEffect(() => {
    const fetchCallData = async () => {
      console.log("Starting fetchCallData for callerIds:", callerIds);
      if (!token || !userId || !vendorId || !campaignId || !callerIds.length) {
        const errorMsg = "Missing authentication or required parameters";
        console.error(errorMsg, {
          token,
          userId,
          vendorId,
          campaignId,
          callerIds,
        });
        const newError = {};
        callerIds.forEach((id) => {
          newError[id] = {
            transcript: errorMsg,
            userInfo: errorMsg,
            leadAnalysis: errorMsg,
            recording: errorMsg,
          };
        });
        setError(newError);
        setLoading(false);
        return;
      }

      try {
        const newLoading = {};
        const newError = {};
        callerIds.forEach((id) => {
          newLoading[id] = {
            transcript: true,
            userInfo: true,
            leadAnalysis: true,
            recording: true,
          };
          newError[id] = {
            transcript: null,
            userInfo: null,
            leadAnalysis: null,
            recording: null,
          };
        });
        setLoading(newLoading);
        setError(newError);

        const fetchDataForCaller = async (callerId) => {
          console.log(
            `Fetching file list for callerId: ${callerId}, URL: ${BASE_API_URL}/${campaignId}/${callerId}`
          );
          let files = [];
          try {
            const filesResponse = await fetch(
              `${BASE_API_URL}/${campaignId}/${callerId}`,
              {
                headers: { Authorization: `Bearer ${token}` },
              }
            );
            if (!filesResponse.ok) {
              throw new Error(
                `Failed to fetch file list for ${callerId}: ${filesResponse.statusText}`
              );
            }
            const responseData = await filesResponse.json();
            files =
              responseData.callerFiles &&
                responseData.callerFiles[callerId] &&
                Array.isArray(responseData.callerFiles[callerId])
                ? responseData.callerFiles[callerId]
                : [];
            console.log(`Files received for ${callerId}:`, files);
          } catch (err) {
            console.error(`Error fetching file list for ${callerId}:`, err);
            throw new Error(`Failed to fetch file list: ${err.message}`);
          }

          // Sort files by timestamp
          const sortedFiles = files.sort((a, b) => {
            const timestampA = a.timestamp || extractTimestamp(a.key);
            const timestampB = b.timestamp || extractTimestamp(b.key);
            return new Date(timestampB) - new Date(timestampA);
          });
          console.log(`Sorted files for ${callerId}:`, sortedFiles);

          const statusFile = sortedFiles.find((file) =>
            file.key.toLowerCase().endsWith("call_status.json")
          );
          const recordingFile = sortedFiles.find((file) =>
            file.key.toLowerCase().endsWith("recording.wav")
          );
          const transcriptFile = sortedFiles.find((file) =>
            file.key.toLowerCase().endsWith("transcript.txt")
          );
          const leadAnalysisFile = sortedFiles.find((file) =>
            file.key.toLowerCase().endsWith("lead_analysis.json")
          );
          console.log(`Files identified for ${callerId}:`, {
            statusFile,
            recordingFile,
            transcriptFile,
            leadAnalysisFile,
          });

          const data = { callerId };

          if (statusFile) {
            try {
              const url = `${S3_BASE_URL}/${statusFile.key}`;
              console.log(`Fetching call status for ${callerId}: ${url}`);
              const statusResponse = await fetch(url);
              if (!statusResponse.ok)
                throw new Error(
                  `Failed to fetch call status: ${statusResponse.statusText}`
                );
              data.userInfo = await statusResponse.json();
              console.log(
                `Call status fetched for ${callerId}:`,
                data.userInfo
              );
            } catch (err) {
              console.error(`Error fetching call status for ${callerId}:`, err);
              newError[callerId].userInfo = err.message;
            }
          } else {
            console.warn(`No call status file found for ${callerId}`);
            newError[callerId].userInfo = "No call status file found";
          }

          if (leadAnalysisFile) {
            try {
              const url = `${S3_BASE_URL}/${leadAnalysisFile.key}`;
              console.log(`Fetching lead analysis for ${callerId}: ${url}`);
              const leadAnalysisResponse = await fetch(url);
              if (!leadAnalysisResponse.ok)
                throw new Error(
                  `Failed to fetch lead analysis: ${leadAnalysisResponse.statusText}`
                );
              data.leadAnalysis = await leadAnalysisResponse.json();
              console.log(
                `Lead analysis fetched for ${callerId}:`,
                data.leadAnalysis
              );
            } catch (err) {
              console.error(
                `Error fetching lead analysis for ${callerId}:`,
                err
              );
              newError[callerId].leadAnalysis = err.message;
            }
          } else {
            console.warn(`No lead analysis file found for ${callerId}`);
            newError[callerId].leadAnalysis = "No lead analysis file found";
          }

          if (transcriptFile) {
            try {
              const url = `${S3_BASE_URL}/${transcriptFile.key}`;
              console.log(`Fetching transcript for ${callerId}: ${url}`);
              const transcriptResponse = await fetch(url);
              if (!transcriptResponse.ok)
                throw new Error(
                  `Failed to fetch transcript: ${transcriptResponse.statusText}`
                );
              const transcriptText = await transcriptResponse.text();
              data.transcript = parseTranscript(transcriptText);
              console.log(
                `Transcript fetched for ${callerId}:`,
                data.transcript
              );
            } catch (err) {
              console.error(`Error fetching transcript for ${callerId}:`, err);
              newError[callerId].transcript = err.message;
            }
          } else {
            console.warn(`No transcript file found for ${callerId}`);
            newError[callerId].transcript = "No transcript file found";
          }

          if (recordingFile) {
            const url = `${S3_BASE_URL}/${recordingFile.key}?t=${Date.now()}`;
            data.recordingUrl = url;
            console.log(`Setting recording URL for ${callerId}:`, url);
          } else {
            console.warn(`No recording file found for ${callerId}`);
            newError[callerId].recording = "No recording file found";
          }

          return data;
        };

        const results = await Promise.all(
          callerIds.map((callerId) =>
            fetchDataForCaller(callerId).catch((err) => {
              console.error(
                `Error fetching data for callerId ${callerId}:`,
                err
              );
              newError[callerId] = {
                transcript: err.message,
                userInfo: err.message,
                leadAnalysis: err.message,
                recording: err.message,
              };
              return null;
            })
          )
        );

        const newCallData = {};
        results.forEach((data) => {
          if (data) {
            newCallData[data.callerId] = data;
          }
        });
        console.log("Updated callData:", newCallData);
        setCallData(newCallData);
        setError(newError);
      } catch (err) {
        console.error("Error fetching call data:", err);
        const newError = {};
        callerIds.forEach((id) => {
          newError[id] = {
            transcript: err.message,
            userInfo: err.message,
            leadAnalysis: err.message,
            recording: err.message,
          };
        });
        setError(newError);
      } finally {
        const newLoading = {};
        callerIds.forEach((id) => {
          newLoading[id] = {
            transcript: false,
            userInfo: false,
            leadAnalysis: false,
            recording: false,
          };
        });
        setLoading(newLoading);
        console.log("Fetch completed, loading state:", newLoading);
      }
    };

    if (campaignData) {
      fetchCallData();
    }
  }, [campaignId, callerIds, vendorId, token, userId, campaignData]);

  const parseTranscript = (text) => {
    if (!text || typeof text !== "string") return [];
    const lines = text.split("\n").filter((line) => line.trim());
    return lines.map((line) => {
      const match = line.match(/^(\w+):\s*(.*)$/);
      return match
        ? { speaker: match[1].trim(), text: match[2].trim() }
        : { speaker: "Unknown", text: line.trim() };
    });
  };

  const extractTimestamp = (key) => {
    const parts = key.split("/");
    const timestampIndex = parts.length - 3; // Assuming timestamp is third from last
    return parts[timestampIndex] || "";
  };

  // Get createdAt for each callerId from parentCallData
  const getCallerIdDisplay = (callerId) => {
    const call = parentCallData.find((c) => c.callerId.includes(callerId));
    if (call && call.createdAt) {
      const date = new Date(call.createdAt);
      return `${date.toLocaleString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
        timeZone: "Asia/Kolkata",
      })}`;
    }
    return callerId;
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>Call Details</h2>
      <div className={styles.callerIdSelector}>
        <label htmlFor="callerIdSelect">All Calls: </label>
        <select
          id="callerIdSelect"
          value={selectedCallerId}
          onChange={(e) => setSelectedCallerId(e.target.value)}
          className={styles.filterSelect}
        >
          {callerIds.map((id) => (
            <option key={id} value={id}>
              {getCallerIdDisplay(id)}
            </option>
          ))}
        </select>
      </div>

      <h3>Lead Summary</h3>
      {loading[selectedCallerId]?.leadAnalysis && (
        <div className={styles.loading}>Loading lead analysis...</div>
      )}
      {error[selectedCallerId]?.leadAnalysis && (
        <div className={styles.error}>
          Error: {error[selectedCallerId].leadAnalysis}
        </div>
      )}
      {!loading[selectedCallerId]?.leadAnalysis &&
        !error[selectedCallerId]?.leadAnalysis &&
        callData[selectedCallerId]?.leadAnalysis ? (
        <div className={styles.leadAnalysis}>
          <p>
            <strong>Lead Type:</strong>{" "}
            {callData[selectedCallerId].leadAnalysis.leadType || "N/A"}
          </p>
          <p>
            <strong>Summary:</strong>{" "}
            {callData[selectedCallerId].leadAnalysis.summary ||
              "No summary available"}
          </p>
          <p>
            <strong>Rating:</strong>{" "}
            {callData[selectedCallerId].leadAnalysis.rating || "N/A"}
          </p>
        </div>
      ) : (
        !loading[selectedCallerId]?.leadAnalysis &&
        !error[selectedCallerId]?.leadAnalysis && (
          <div>No lead analysis available</div>
        )
      )}

      <div className={styles.tabContainer}>
        <button
          className={`${styles.tab} ${activeTab === "transcript" ? styles.activeTab : ""
            }`}
          onClick={() => setActiveTab("transcript")}
          aria-selected={activeTab === "transcript"}
          role="tab"
        >
          Transcript
        </button>
        <button
          className={`${styles.tab} ${activeTab === "userInfo" ? styles.activeTab : ""
            }`}
          onClick={() => setActiveTab("userInfo")}
          aria-selected={activeTab === "userInfo"}
          role="tab"
        >
          User Detail
        </button>
        <button
          className={`${styles.tab} ${activeTab === "recording" ? styles.activeTab : ""
            }`}
          onClick={() => setActiveTab("recording")}
          aria-selected={activeTab === "recording"}
          role="tab"
        >
          Call Recording
        </button>
      </div>

      <div className={styles.content}>
        {activeTab === "transcript" && (
          <div className={styles.transcript}>
            {loading[selectedCallerId]?.transcript && (
              <div className={styles.loading}>Loading transcript...</div>
            )}
            {error[selectedCallerId]?.transcript && (
              <div className={styles.error}>
                Error: {error[selectedCallerId].transcript}
              </div>
            )}
            {!loading[selectedCallerId]?.transcript &&
              !error[selectedCallerId]?.transcript &&
              (callData[selectedCallerId]?.transcript?.length > 0 ? (
                callData[selectedCallerId].transcript.map((entry, index) => (
                  <div
                    key={index}
                    className={`${styles.message} ${entry.speaker === "Agent"
                      ? styles.agentMessage
                      : styles.userMessage
                      }`}
                  >
                    <span className={styles.speaker}>{entry.speaker}:</span>
                    <span className={styles.messageText}>{entry.text}</span>
                  </div>
                ))
              ) : (
                <div>No transcript available</div>
              ))}
          </div>
        )}

        {activeTab === "userInfo" && (
          <div className={styles.userInfo}>
            {loading[selectedCallerId]?.userInfo && (
              <div className={styles.loading}>Loading user info...</div>
            )}
            {error[selectedCallerId]?.userInfo && (
              <div className={styles.error}>
                Error: {error[selectedCallerId].userInfo}
              </div>
            )}
            {!loading[selectedCallerId]?.userInfo &&
              !error[selectedCallerId]?.userInfo &&
              callData[selectedCallerId]?.userInfo && (
                <table className={styles.infoTable}>
                  <thead>
                    <tr>
                      <th scope="col">Key</th>
                      <th scope="col">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Call ID</td>
                      <td>
                        {callData[selectedCallerId].userInfo.callId || "N/A"}
                      </td>
                    </tr>
                    <tr>
                      <td>Caller ID</td>
                      <td>{selectedCallerId}</td>
                    </tr>
                    <tr>
                      <td>Vendor ID</td>
                      <td>{vendorId}</td>
                    </tr>
                    <tr>
                      <td>Campaign ID</td>
                      <td>{campaignId}</td>
                    </tr>
                    <tr>
                      <td>User ID</td>
                      <td>{userId}</td>
                    </tr>
                    <tr>
                      <td>Client Version</td>
                      <td>
                        {callData[selectedCallerId].userInfo.clientVersion ||
                          "N/A"}
                      </td>
                    </tr>
                    <tr>
                      <td>Created</td>
                      <td>
                        {callData[selectedCallerId].userInfo.created
                          ? new Date(
                            callData[selectedCallerId].userInfo.created
                          ).toLocaleString()
                          : "N/A"}
                      </td>
                    </tr>
                    <tr>
                      <td>Joined</td>
                      <td>
                        {callData[selectedCallerId].userInfo.joined
                          ? new Date(
                            callData[selectedCallerId].userInfo.joined
                          ).toLocaleString()
                          : "N/A"}
                      </td>
                    </tr>
                    <tr>
                      <td>Ended</td>
                      <td>
                        {callData[selectedCallerId].userInfo.ended
                          ? new Date(
                            callData[selectedCallerId].userInfo.ended
                          ).toLocaleString()
                          : "N/A"}
                      </td>
                    </tr>
                    <tr>
                      <td>End Reason</td>
                      <td>
                        {callData[selectedCallerId].userInfo.endReason || "N/A"}
                      </td>
                    </tr>
                    <tr>
                      <td>Billed Duration</td>
                      <td>
                        {callData[selectedCallerId].userInfo.billedDuration ||
                          "N/A"}
                      </td>
                    </tr>
                    <tr>
                      <td>First Speaker</td>
                      <td>
                        {callData[selectedCallerId].userInfo.firstSpeaker ||
                          "N/A"}
                      </td>
                    </tr>
                    <tr>
                      <td>Language Hint</td>
                      <td>
                        {callData[selectedCallerId].userInfo.languageHint ||
                          "N/A"}
                      </td>
                    </tr>
                    <tr>
                      <td>Max Duration</td>
                      <td>
                        {callData[selectedCallerId].userInfo.maxDuration ||
                          "N/A"}
                      </td>
                    </tr>
                    <tr>
                      <td>Model</td>
                      <td>
                        {callData[selectedCallerId].userInfo.model || "N/A"}
                      </td>
                    </tr>
                    <tr>
                      <td>Recording Enabled</td>
                      <td>
                        {callData[selectedCallerId].userInfo.recordingEnabled
                          ? "Yes"
                          : "No"}
                      </td>
                    </tr>
                    <tr>
                      <td>System Prompt</td>
                      <td>
                        {callData[selectedCallerId].userInfo.systemPrompt ||
                          "N/A"}
                      </td>
                    </tr>
                    <tr>
                      <td>Temperature</td>
                      <td>
                        {callData[selectedCallerId].userInfo.temperature ||
                          "N/A"}
                      </td>
                    </tr>
                    <tr>
                      <td>Voice ID</td>
                      <td>
                        {callData[selectedCallerId].userInfo.voice || "N/A"}
                      </td>
                    </tr>
                    <tr>
                      <td>Short Summary</td>
                      <td>
                        {callData[selectedCallerId].userInfo.shortSummary ||
                          "N/A"}
                      </td>
                    </tr>
                    <tr>
                      <td>Inactivity Message</td>
                      <td>
                        {callData[selectedCallerId].userInfo
                          .inactivityMessages?.[0]?.message || "N/A"}
                      </td>
                    </tr>
                    <tr>
                      <td>Inactivity Duration</td>
                      <td>
                        {callData[selectedCallerId].userInfo
                          .inactivityMessages?.[0]?.duration || "N/A"}
                      </td>
                    </tr>
                    <tr>
                      <td>Initial Output Medium</td>
                      <td>
                        {callData[selectedCallerId].userInfo
                          .initialOutputMedium || "N/A"}
                      </td>
                    </tr>
                    <tr>
                      <td>Join Timeout</td>
                      <td>
                        {callData[selectedCallerId].userInfo.joinTimeout ||
                          "N/A"}
                      </td>
                    </tr>
                    <tr>
                      <td>Time Exceeded Message</td>
                      <td>
                        {callData[selectedCallerId].userInfo
                          .timeExceededMessage || "N/A"}
                      </td>
                    </tr>
                    <tr>
                      <td>Error Count</td>
                      <td>
                        {callData[selectedCallerId].userInfo.errorCount ||
                          "N/A"}
                      </td>
                    </tr>
                    <tr>
                      <td>Plivo Call ID</td>
                      <td>
                        {callData[selectedCallerId].userInfo.metadata?.[
                          "ultravox.plivo.call_id"
                        ] || "N/A"}
                      </td>
                    </tr>
                    <tr>
                      <td>Plivo Stream ID</td>
                      <td>
                        {callData[selectedCallerId].userInfo.metadata || "N/A"}
                      </td>
                    </tr>
                    <tr>
                      <td>Plivo Account ID</td>
                      <td>
                        {callData[selectedCallerId].userInfo.metadata?.[
                          "ultravox.plivo.account_id"
                        ] || "N/A"}
                      </td>
                    </tr>
                  </tbody>
                </table>
              )}
            {!loading[selectedCallerId]?.userInfo &&
              !error[selectedCallerId]?.userInfo &&
              !callData[selectedCallerId]?.userInfo && (
                <div>No user information available</div>
              )}
          </div>
        )}

        {activeTab === "recording" && (
          <div className={styles.recording}>
            <h3 className={styles.recordingTitle}>Call Recording</h3>
            {loading[selectedCallerId]?.recording && (
              <div className={styles.loading}>Loading recording...</div>
            )}
            {error[selectedCallerId]?.recording && (
              <div className={styles.error}>
                Error: {error[selectedCallerId].recording}
              </div>
            )}
            {!loading[selectedCallerId]?.recording &&
              !error[selectedCallerId]?.recording &&
              callData[selectedCallerId]?.recordingUrl ? (
              <audio
                controls
                className={styles.audioPlayer}
                preload="metadata"
                aria-label="Call recording playback"
                onError={(e) => {
                  console.error("Audio error:", e.nativeEvent);
                  setError((prev) => ({
                    ...prev,
                    [selectedCallerId]: {
                      ...prev[selectedCallerId],
                      recording: "Failed to load audio file",
                    },
                  }));
                }}
              >
                <source
                  src={callData[selectedCallerId].recordingUrl}
                  type="audio/wav"
                />
                Your browser does not support the audio element.
              </audio>
            ) : (
              !loading[selectedCallerId]?.recording &&
              !error[selectedCallerId]?.recording && (
                <div>No recording available</div>
              )
            )}
          </div>
        )}
      </div>
      <button onClick={onClose} className={styles.closeBtn}>
        X
      </button>
    </div>
  );
};

// Main CallHistory component
const CallHistory = ({ campaignId }) => {
  const navigate = useNavigate();
  const [callData, setCallData] = useState([]);
  const [leadAnalysisData, setLeadAnalysisData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [durationFilter, setDurationFilter] = useState("");
  const [ratingFilter, setRatingFilter] = useState("");
  const [leadTypeFilter, setLeadTypeFilter] = useState("");
  const [dealStatusFilter, setDealStatusFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCalls, setSelectedCalls] = useState([]);
  const [notes, setNotes] = useState({});
  const [dealStatuses, setDealStatuses] = useState({});
  const [schedulerCall, setSchedulerCall] = useState(null);
  const [selectedCallDetails, setSelectedCallDetails] = useState(null);
  const [showStatusFilter, setShowStatusFilter] = useState(false);
  const [showDurationFilter, setShowDurationFilter] = useState(false);
  const [showRatingFilter, setShowRatingFilter] = useState(false);
  const [showLeadTypeFilter, setShowLeadTypeFilter] = useState(false);
  const [showDealStatusFilter, setShowDealStatusFilter] = useState(false);
  const [showNotesPopup, setShowNotesPopup] = useState(null);
  const token = JSON.parse(localStorage.getItem("authData") || "{}")?.token;
  const userId = JSON.parse(localStorage.getItem("authData") || "{}")?.user?.id;
  const userRole = JSON.parse(localStorage.getItem("authData") || "{}")?.user
    ?.role;
  const BASE_API_URL =
    "https://prod.toxboxai.com/api/call-history/files";
  const S3_BASE_URL = "https://toxbox-images.s3.ap-south-1.amazonaws.com";

  useEffect(() => {
    if (!token || !userRole || !userId) {
      console.log("Missing authentication data:", { token, userRole, userId });
      setError("Please log in to continue");
      setLoading(false);
      return;
    }

    console.log("Campaign", campaignId);

    // Set up SSE connection
    const eventSource = new EventSource(
      `https://prod.toxboxai.com/api/sheetdata/live-data/${campaignId}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    // Fallback timeout to prevent indefinite loading
    const timeout = setTimeout(() => {
      console.warn("SSE connection timed out after 30 seconds");
      setError("Connection to live data feed timed out");
      setLoading(false);
      eventSource.close();
    }, 30000); // 30-second timeout

    let eventIndex = 0;

    const fetchLeadAnalysis = async (callerIds) => {
      console.log("Starting fetchLeadAnalysis for callerIds:", callerIds);
      try {
        const results = await Promise.all(
          callerIds.map(async (callerId) => {
            if (!callerId || callerId === "N/A" || callerId === "") {
              console.warn(
                `Invalid callerId: ${callerId}, returning default data`
              );
              return {
                callerId,
                leadType: "N/A",
                summary: "No summary available",
                rating: "N/A",
              };
            }

            console.log(
              `Fetching file list for callerId: ${callerId}, URL: ${BASE_API_URL}/${campaignId}/${callerId}`
            );
            const filesResponse = await fetch(
              `${BASE_API_URL}/${campaignId}/${callerId}`,
              {
                headers: { Authorization: `Bearer ${token}` },
              }
            );
            if (!filesResponse.ok) {
              throw new Error(
                `Failed to fetch file list for ${callerId}: ${filesResponse.statusText}`
              );
            }
            const responseData = await filesResponse.json();
            const files =
              responseData.callerFiles &&
                responseData.callerFiles[callerId] &&
                Array.isArray(responseData.callerFiles[callerId])
                ? responseData.callerFiles[callerId]
                : [];
            console.log(`Files received for ${callerId}:`, files);

            const leadAnalysisFile = files.find((file) =>
              file.key.toLowerCase().endsWith("lead_analysis.json")
            );
            console.log(
              `Lead analysis file for ${callerId}:`,
              leadAnalysisFile
            );

            if (leadAnalysisFile) {
              try {
                const url = `${S3_BASE_URL}/${leadAnalysisFile.key}`;
                console.log(`Fetching lead analysis for ${callerId}: ${url}`);
                const leadAnalysisResponse = await fetch(url);
                if (!leadAnalysisResponse.ok) {
                  throw new Error(
                    `Failed to fetch lead analysis: ${leadAnalysisResponse.statusText}`
                  );
                }
                const leadAnalysisData = await leadAnalysisResponse.json();
                console.log(
                  `Lead analysis fetched for ${callerId}:`,
                  leadAnalysisData
                );
                return { callerId, ...leadAnalysisData };
              } catch (err) {
                console.error(
                  `Error fetching lead analysis for ${callerId}:`,
                  err
                );
                return {
                  callerId,
                  leadType: "N/A",
                  summary: "No summary available",
                  rating: "N/A",
                };
              }
            }
            console.warn(`No lead analysis file found for ${callerId}`);
            return {
              callerId,
              leadType: "N/A",
              summary: "No summary available",
              rating: "N/A",
            };
          })
        );
        console.log("Lead analysis results:", results);
        return results;
      } catch (err) {
        console.error(
          `Error fetching lead analysis for callerIds ${callerIds.join(", ")}:`,
          err
        );
        return callerIds.map((callerId) => ({
          callerId,
          leadType: "N/A",
          summary: "No summary available",
          rating: "N/A",
        }));
      }
    };

    eventSource.onmessage = async (event) => {
      try {
        // Clear timeout since we received a message
        clearTimeout(timeout);

        if (!event.data || event.data === "null") {
          console.warn("Received empty or null event data");
          setLoading(false);
          setCallData([]);
          return;
        }

        let eventData;
        try {
          eventData = JSON.parse(event.data);
        } catch (parseError) {
          console.error(
            "JSON parse error:",
            parseError,
            "Raw data:",
            event.data
          );
          setError("Invalid JSON format from live data feed");
          setLoading(false);
          return;
        }

        // Handle the new response format with { type, data }
        if (!eventData.type || !eventData.data) {
          console.warn(
            "Invalid event data format, missing type or data:",
            eventData
          );
          setError("Invalid data format received from live data feed");
          setLoading(false);
          setCallData([]);
          return;
        }

        const dataArray = Array.isArray(eventData.data)
          ? eventData.data
          : [eventData.data];

        if (dataArray.length === 0) {
          console.log("No call data received from API");
          setLoading(false);
          setCallData([]);
          return;
        }

        const newCallData = [];
        for (const entry of dataArray) {
          // Skip entries without _id
          if (!entry._id) {
            console.warn("Skipping entry with missing _id:", entry);
            continue;
          }

          try {
            const sanitizedEntry = sanitizeCallData(entry);
            const newLeadAnalysisData = await fetchLeadAnalysis(
              sanitizedEntry.callerId
            );
            setLeadAnalysisData((prev) => {
              const existingIds = new Set(prev.map((item) => item.callerId));
              const filteredNewData = newLeadAnalysisData.filter(
                (item) => !existingIds.has(item.callerId)
              );
              return [...prev, ...filteredNewData];
            });

            const callDataEntry = getCallDataFromApi(
              entry,
              campaignId,
              eventIndex,
              newLeadAnalysisData
            );
            newCallData.push(callDataEntry);
          } catch (entryError) {
            console.error(
              `Error processing entry ${eventIndex}:`,
              entryError,
              "Entry:",
              entry
            );
          }
          eventIndex++;
        }

        setCallData((prev) => {
          const updatedData = [...prev];
          newCallData.forEach((newEntry) => {
            const existingIndex = updatedData.findIndex(
              (item) => item.id === newEntry.id
            );
            if (existingIndex >= 0) {
              updatedData[existingIndex] = newEntry; // Update existing entry
            } else {
              updatedData.push(newEntry); // Add new entry
            }
          });
          return updatedData;
        });
        setError("");
        setLoading(false);
      } catch (e) {
        console.error("Error processing event data:", e);
        setError(`Failed to parse live data: ${e.message}`);
        setLoading(false);
        clearTimeout(timeout);
      }
    };

    eventSource.onopen = () => {
      console.log("SSE connection opened");
    };

    eventSource.onerror = (err) => {
      console.error("EventSource error:", err);
      setError("Failed to connect to live data feed");
      setLoading(false);
      eventSource.close();
      clearTimeout(timeout);
    };

    return () => {
      clearTimeout(timeout);
      eventSource.close();
    };
  }, [token, userRole, userId, campaignId]);

  // Calculate metrics for cards
  const totalCalls = callData.length;
  const joinedCalls = callData.filter(
    (call) => call.callStatus === "Joined"
  ).length;
  const unjoinedCalls = callData.filter(
    (call) => call.callStatus === "Unjoined"
  ).length;
  const coldLeads = callData.filter((call) => call.leadType === "cold").length;
  const warmCalls = callData.filter((call) => call.leadType === "warm").length;

  // Filter data based on specified filters and search query
  const filteredData = callData.filter((call) => {
    const statusMatch = statusFilter
      ? statusFilter === "Unjoined"
        ? call.callStatus === "Unjoined"
        : call.callStatus === statusFilter
      : true;
    const durationMatch = durationFilter
      ? durationFilter === "Less than 1 min"
        ? call.callDuration < 60
        : formatDuration(call.callDuration).startsWith(durationFilter)
      : true;
    const ratingMatch = ratingFilter ? call.callRating === ratingFilter : true;
    const leadTypeMatch = leadTypeFilter
      ? call.leadType === leadTypeFilter
      : true;
    const dealStatusMatch = dealStatusFilter
      ? call.dealStatus === dealStatusFilter
      : true;
    const searchMatch = searchQuery
      ? call.name.toLowerCase().includes((searchQuery || "").toLowerCase()) ||
      call.number.includes(searchQuery) ||
      call.callerId.some((id) =>
        id.toLowerCase().includes((searchQuery || "").toLowerCase())
      )
      : true;
    return (
      statusMatch &&
      durationMatch &&
      ratingMatch &&
      leadTypeMatch &&
      dealStatusMatch &&
      searchMatch
    );
  });

  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const handleShowDetails = (call) => {
    console.log(
      "Showing details for call:",
      call.id,
      "with callerIds:",
      call.callerId
    );
    setSelectedCallDetails({
      id: call.id,
      callerIds: call.callerId,
      campaignId,
      callData,
    });
  };

  const handleCloseDetails = () => {
    setSelectedCallDetails(null);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const handleCheckboxChange = (callId) => {
    setSelectedCalls((prev) =>
      prev.includes(callId)
        ? prev.filter((id) => id !== callId)
        : [...prev, callId]
    );
  };

  const handleMoveToCallerBook = async () => {
    if (selectedCalls.length === 0) {
      console.log("Please select at least one call to move to Calling Book");
      return;
    }

    try {
      const token = JSON.parse(localStorage.getItem("authData") || "{}")?.token;
      const movePromises = selectedCalls.map(async (callId) => {
        const call = callData.find((c) => c.id === callId);
        if (!call) {
          throw new Error(`Call with ID ${callId} not found`);
        }
        const payload = {
          campaignId: call.campaignId,
          phoneNumber: call.number,
        };
        const response = await axios.post(
          "https://prod.toxboxai.com/api/move-to-caller/move-to-calling-book",
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        return { callId, response };
      });

      const results = await Promise.allSettled(movePromises);
      const successes = results.filter(
        (result) => result.status === "fulfilled"
      );
      const failures = results.filter((result) => result.status === "rejected");
      window.location.reload();

      if (successes.length === selectedCalls.length) {
        console.log("All selected calls moved to Calling Book successfully");
        setSelectedCalls([]);
      } else {
        const errorMessages = failures.map((f) => f.reason.message).join("; ");
        console.log(`Some calls failed to move: ${errorMessages}`);
      }
    } catch (err) {
      console.error("Error moving calls to Calling Book:", err);
      console.log("Error moving calls to Calling Book: " + err.message);
    }
  };

  const handleNotesChange = (callId, value) => {
    setNotes((prev) => ({ ...prev, [callId]: value }));
  };

  const handleDealStatusChange = (callId, value) => {
    setDealStatuses((prev) => ({ ...prev, [callId]: value }));
    setCallData((prev) =>
      prev.map((call) =>
        call.id === callId ? { ...call, dealStatus: value } : call
      )
    );
  };

  const handleNotesAndDealStatusBlur = async (callId) => {
    const note = notes[callId];
    const dealStatus = dealStatuses[callId];

    if (!note && !dealStatus) {
      return; // No changes to save
    }

    try {
      const token = JSON.parse(localStorage.getItem("authData") || "{}")?.token;
      const payload = {};
      if (note && note.trim()) {
        payload.notes = note.trim();
      }
      if (dealStatus && dealStatus !== "N/A") {
        payload.dealStatus = dealStatus;
      }

      const response = await axios.post(
        `https://prod.toxboxai.com/api/sheetdata/add-note/${callId}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        console.log("Notes and deal status saved for call:", callId);
        setCallData((prev) =>
          prev.map((call) =>
            call.id === callId
              ? {
                ...call,
                notes: response.data.data.sheetData.notes,
                dealStatus: response.data.data.sheetData.dealStatus,
              }
              : call
          )
        );
        setNotes((prev) => ({ ...prev, [callId]: "" })); // Clear input after save
      } else {
        console.log(
          "Failed to save notes and deal status: " + response.data.error
        );
      }
    } catch (err) {
      console.log("Error saving notes and deal status: " + err.message);
    }
  };

  const handleScheduleMeeting = (call) => {
    setSchedulerCall(call);
  };

  const handleCardClick = (filterType, value) => {
    if (filterType === "status") {
      setStatusFilter(value);
      setLeadTypeFilter("");
      setCurrentPage(1);
    } else if (filterType === "leadType") {
      setLeadTypeFilter(value);
      setStatusFilter("");
      setCurrentPage(1);
    } else {
      setStatusFilter("");
      setLeadTypeFilter("");
      setCurrentPage(1);
    }
  };

  const statusFilterOptions = [
    { value: "", label: "All Statuses" },
    { value: "Joined", label: "Joined" },
    { value: "Unjoined", label: "Unjoined" },
  ];

  const durationFilterOptions = [
    { value: "", label: "All Durations" },
    { value: "Less than 1 min", label: "Less than 1 min" },
    { value: "2 min", label: "2 min" },
    { value: "3 min", label: "3 min" },
    { value: "4 min", label: "4 min" },
  ];

  const ratingFilterOptions = [
    { value: "", label: "All Ratings" },
    { value: "1", label: "1" },
    { value: "2", label: "2" },
    { value: "3", label: "3" },
    { value: "4", label: "4" },
    { value: "5", label: "5" },
  ];

  const leadTypeFilterOptions = [
    { value: "", label: "All Lead Types" },
    { value: "warm", label: "Warm" },
    { value: "cold", label: "Cold" },
  ];

  const dealStatusFilterOptions = [
    { value: "", label: "All Deal Statuses" },
    { value: "Deal Closed", label: "Deal Closed" },
    { value: "Site Visit", label: "Site Visit" },
    { value: "Not Visited Yet", label: "Not Visited Yet" },
  ];

  const dealStatusOptions = [
    { value: "N/A", label: "Select Deal Status" },
    { value: "Deal Closed", label: "Deal Closed" },
    { value: "Site Visit", label: "Site Visit" },
    { value: "Not Visited Yet", label: "Not Visited Yet" },
  ];

  return (
    <div className={styles.callHistory}>
      <div className={styles.header}>
        <h2>Call Results</h2>
      </div>
      {loading && <LoaderSpiner />}
      {!loading && error && <p className={styles.error}>{error}</p>}
      {!loading && !error && callData.length === 0 && (
        <p className={styles.noData}>No calls found for this campaign.</p>
      )}
      {!loading && !error && callData.length > 0 && (
        <>
          <div className={styles.cardsContainer}>
            <div
              className={styles.card}
              onClick={() => handleCardClick("all", "")}
            >
              <h3>Total Calls</h3>
              <p>{totalCalls}</p>
            </div>
            <div
              className={styles.card}
              onClick={() => handleCardClick("status", "Joined")}
            >
              <h3>Joined Calls</h3>
              <p>{joinedCalls}</p>
            </div>
            <div
              className={styles.card}
              onClick={() => handleCardClick("status", "Unjoined")}
            >
              <h3>Unjoined Calls</h3>
              <p>{unjoinedCalls}</p>
            </div>
            <div
              className={styles.card}
              onClick={() => handleCardClick("leadType", "cold")}
            >
              <h3>Cold Leads</h3>
              <p>{coldLeads}</p>
            </div>
            <div
              className={styles.card}
              onClick={() => handleCardClick("leadType", "warm")}
            >
              <h3>Warm Calls</h3>
              <p>{warmCalls}</p>
            </div>
          </div>
          <div className={styles.filters}>
            <div className={styles.searchBox}>
              <span className={styles.searchIcon}>🔍</span>
              <input
                type="text"
                placeholder="Search by name, number, or caller ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={styles.searchInput}
              />
            </div>
            <button
              onClick={handleMoveToCallerBook}
              className={styles.moveButton}
              disabled={selectedCalls.length === 0}
            >
              Move to Calling Book
            </button>
          </div>
          <div className={styles.tableContainer}>
            <table className={styles.table} style={{ width: "100rem" }}>
              <thead>
                <tr>
                  <th className={styles.checkboxTh}>
                    <input
                      type="checkbox"
                      checked={
                        selectedCalls.length === paginatedData.length &&
                        paginatedData.length > 0
                      }
                      onChange={() => {
                        if (selectedCalls.length === paginatedData.length) {
                          setSelectedCalls([]);
                        } else {
                          setSelectedCalls(
                            paginatedData.map((call) => call.id)
                          );
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </th>
                  <th>Name</th>
                  <th>Phone Number</th>
                  <th>Start Time</th>
                  <th className={styles.filterTh}>
                    Call Status
                    <button
                      className={styles.filterIcon}
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowStatusFilter(!showStatusFilter);
                        setShowDurationFilter(false);
                        setShowRatingFilter(false);
                        setShowLeadTypeFilter(false);
                        setShowDealStatusFilter(false);
                      }}
                    >
                      <FaFilter />
                    </button>
                    {showStatusFilter && (
                      <FilterMenu
                        options={statusFilterOptions}
                        onSelect={(value) => {
                          setStatusFilter(value);
                          setLeadTypeFilter("");
                          setCurrentPage(1);
                        }}
                        onClose={() => setShowStatusFilter(false)}
                        currentFilter={statusFilter}
                      />
                    )}
                  </th>
                  <th className={styles.filterTh}>
                    Lead Type
                    <button
                      className={styles.filterIcon}
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowLeadTypeFilter(!showLeadTypeFilter);
                        setShowStatusFilter(false);
                        setShowDurationFilter(false);
                        setShowRatingFilter(false);
                        setShowDealStatusFilter(false);
                      }}
                    >
                      <FaFilter />
                    </button>
                    {showLeadTypeFilter && (
                      <FilterMenu
                        options={leadTypeFilterOptions}
                        onSelect={(value) => {
                          setLeadTypeFilter(value);
                          setStatusFilter("");
                          setCurrentPage(1);
                        }}
                        onClose={() => setShowLeadTypeFilter(false)}
                        currentFilter={leadTypeFilter}
                      />
                    )}
                  </th>
                  <th className={styles.filterTh}>
                    Deal Status
                    <button
                      className={styles.filterIcon}
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowDealStatusFilter(!showDealStatusFilter);
                        setShowStatusFilter(false);
                        setShowDurationFilter(false);
                        setShowRatingFilter(false);
                        setShowLeadTypeFilter(false);
                      }}
                    >
                      <FaFilter />
                    </button>
                    {showDealStatusFilter && (
                      <FilterMenu
                        options={dealStatusFilterOptions}
                        onSelect={(value) => {
                          setDealStatusFilter(value);
                          setCurrentPage(1);
                        }}
                        onClose={() => setShowDealStatusFilter(false)}
                        currentFilter={dealStatusFilter}
                      />
                    )}
                  </th>
                  <th>Notes</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((call, index) => (
                  <tr className={styles.callRow} key={index}>
                    <td className={styles.checkboxTd}>
                      <input
                        type="checkbox"
                        checked={selectedCalls.includes(call.id)}
                        onChange={() => handleCheckboxChange(call.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td>
                      <span
                        className={styles.callIdCell}
                        onClick={() => handleShowDetails(call)}
                      >
                        {call.name}
                      </span>
                    </td>
                    <td>{call.number}</td>
                    <td>{call.startTime}</td>
                    <td>{call.callStatus}</td>
                    <td>{call.leadType}</td>
                    <td>
                      <select
                        value={
                          dealStatuses[call.id] || call.dealStatus || "N/A"
                        }
                        onChange={(e) =>
                          handleDealStatusChange(call.id, e.target.value)
                        }
                        onBlur={() => handleNotesAndDealStatusBlur(call.id)}
                        className={styles.dealStatusSelect}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {dealStatusOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className={styles.notesTd}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowNotesPopup(call.id);
                        }}
                        className={styles.notesButton}
                      >
                        <LiaNotesMedicalSolid />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleScheduleMeeting(call);
                        }}
                        className={styles.scheduleButton}
                      >
                        <FaCalendar />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className={styles.pagination}>
            <select
              value={pageSize}
              onChange={handlePageSizeChange}
              className={styles.pageSizeSelect}
            >
              <option value={10}>10 per page</option>
              <option value={20}>20 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </select>
            <button
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
              className={styles.pageButton}
            >
              Previous
            </button>
            <span className={styles.pageInfo}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className={styles.pageButton}
            >
              Next
            </button>
          </div>
          <AnimatePresence>
            {schedulerCall && (
              <motion.div
                className={styles.modalOverlay}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <div
                  onClick={() => setSchedulerCall(null)}
                  style={{
                    position: "absolute",
                    top: "10px",
                    right: "10px",
                    color: "#fff",
                    background: "red",
                    padding: "0.5rem 1rem",
                    borderRadius: "20px",
                  }}
                >
                  X
                </div>
                <CalComponent />
              </motion.div>
            )}
            {selectedCallDetails && (
              <motion.div
                className={styles.modalOverlay}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <CallDetails
                  {...selectedCallDetails}
                  onClose={handleCloseDetails}
                />
              </motion.div>
            )}
            {showNotesPopup && (
              <motion.div
                className={styles.modalOverlay}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <NotesPopup
                  call={paginatedData.find(
                    (call) => call.id === showNotesPopup
                  )}
                  notes={notes}
                  onNotesChange={handleNotesChange}
                  onNotesBlur={handleNotesAndDealStatusBlur}
                  onClose={() => setShowNotesPopup(null)}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
    </div>
  );
};

export default CallHistory;
